import React, { useEffect, useState, useCallback } from "react";
import { api, describeError } from "../lib/api";

const INK = "#141412";
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }); } catch { return iso; } };
const userEmail = () => { try { return JSON.parse(sessionStorage.getItem("revq.user") || "{}").email || ""; } catch { return ""; } };

const Mark = ({ size = 26 }) => (
  <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: size, height: size, borderRadius: 8, background: INK }}>
    <img src="/revQ_emblem_black.webp" alt="Aria One" style={{ width: size * 0.62, height: size * 0.62, objectFit: "contain", filter: "invert(1) brightness(2)" }} />
  </span>
);

const navBtn = (disabled) => ({
  fontSize: 12, fontWeight: 700, color: disabled ? "#B8B8B4" : INK,
  background: "#F2F2F0", border: "1px solid #E2E2DF", borderRadius: 999,
  padding: "8px 14px", cursor: disabled ? "default" : "pointer",
});

/* Renders the current Business Associate Agreement.
   variant="inline"    → embedded reader (Settings / Login) — read-only or accept.
   variant="workspace" → full-screen gate: sticky header + scrolling agreement + sticky sign footer. */
export function BaaReader({ showAccept = false, initialEntity = "", variant = "inline", onAccepted, onClose }) {
  const [doc, setDoc] = useState(null);
  const [status, setStatus] = useState(null);
  const [entity, setEntity] = useState(initialEntity || "");
  const [signer, setSigner] = useState("");
  const [pct, setPct] = useState(0);
  const [atEnd, setAtEnd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [confirm, setConfirm] = useState(false);

  useEffect(() => {
    api.get("/api/edi/baa/current").then((r) => setDoc(r.data)).catch((e) => setErr(describeError(e)));
    if (showAccept) api.get("/api/edi/baa/status").then((r) => setStatus(r.data)).catch(() => {});
  }, [showAccept]);

  const onScroll = useCallback((e) => {
    const el = e.currentTarget;
    const max = el.scrollHeight - el.clientHeight;
    const p = max <= 4 ? 100 : Math.min(100, Math.round((el.scrollTop / max) * 100));
    setPct(p);
    if (p >= 98) setAtEnd(true);
  }, []);

  const download = useCallback(async () => {
    try {
      const r = await api.get("/api/edi/baa/download", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `AriaOne-BAA-v${doc?.version || ""}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setErr(describeError(e)); }
  }, [doc]);

  const accept = useCallback(async () => {
    setBusy(true); setErr("");
    try {
      const r = await api.post("/api/edi/baa/accept", { covered_entity_name: entity.trim(), baa_version: doc?.version });
      onAccepted?.(r.data);
    } catch (e) { setErr(describeError(e)); setConfirm(false); } finally { setBusy(false); }
  }, [entity, doc, onAccepted]);

  const version = doc?.version;
  const reaccept = showAccept && status?.accepted && status?.accepted_version && version && status.accepted_version !== version;
  const canAccept = atEnd && entity.trim() && signer.trim();
  const acceptLabel = !atEnd ? "Read to the end to sign" : !entity.trim() || !signer.trim() ? "Enter the practice and your name" : "I have read and accept this agreement";
  const workspace = variant === "workspace";

  // ---- shared pieces ----------------------------------------------------
  const docContent = doc ? (
    <>
      {(doc.pages || []).map((pg, pi) => (
        <div key={pi} style={{ marginBottom: 6 }}>
          {pg.title ? <div style={{ fontWeight: 800, fontSize: 14, margin: pi ? "18px 0 8px" : "0 0 8px", color: INK }}>{pg.title}</div> : null}
          {(pg.blocks || []).map((b, i) =>
            b.type === "h" ? (
              <div key={i} style={{ fontWeight: 800, margin: "14px 0 6px", color: INK }}>{b.text}</div>
            ) : b.type === "li" ? (
              <div key={i} style={{ display: "flex", gap: 8, margin: "4px 0 4px 6px" }}>
                <span style={{ color: "#9B9B97" }}>•</span><span>{b.text}</span>
              </div>
            ) : (
              <p key={i} style={{ margin: "0 0 10px" }}>{b.text}</p>
            )
          )}
        </div>
      ))}
      <div data-testid="baa-endmark" style={{ textAlign: "center", fontSize: 11, letterSpacing: ".08em", textTransform: "uppercase", color: "#B8B8B4", borderTop: "1px solid #EEEEEC", marginTop: 8, paddingTop: 12 }}>End of agreement</div>
    </>
  ) : (
    <div style={{ color: "#9B9B97" }}>{err || "Loading agreement…"}</div>
  );

  const reacceptBanner = reaccept ? (
    <div data-testid="baa-reaccept" style={{ fontSize: 12.5, lineHeight: 1.5, color: "#3A3A36", background: "#FFF9EE", border: "1px solid #F1E2C4", borderRadius: 10, padding: "10px 12px" }}>
      <b>You accepted version {status.accepted_version} on {fmtDate(status.accepted_at)}.</b> This is version {version}. Please read the current agreement in full before accepting it on behalf of your practice.
    </div>
  ) : null;

  const signFields = (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
        <label style={{ fontSize: 11.5, fontWeight: 700, color: INK }}>Covered entity — the practice being bound</label>
        <input value={entity} onChange={(e) => setEntity(e.target.value)} placeholder="Legal name of the practice" data-testid="baa-entity-input"
          style={{ padding: "10px 12px", border: "1px solid #E2E2DF", borderRadius: 10, fontSize: 13.5, outline: "none", minWidth: 0, width: "100%", boxSizing: "border-box" }} />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
        <label style={{ fontSize: 11.5, fontWeight: 700, color: INK }}>Your full name — you are attesting</label>
        <input value={signer} onChange={(e) => setSigner(e.target.value)} placeholder="Type your name to sign" data-testid="baa-signer-input"
          style={{ padding: "10px 12px", border: "1px solid #E2E2DF", borderRadius: 10, fontSize: 13.5, outline: "none", minWidth: 0, width: "100%", boxSizing: "border-box" }} />
      </div>
    </div>
  );

  const attest = (
    <div data-testid="baa-attest" style={{ fontSize: 12.5, lineHeight: 1.55, color: "#3A3A36", background: "#F7F7F6", borderRadius: 10, padding: "10px 12px" }}>
      I, <b style={{ color: canAccept || signer ? INK : "#B0B0AC" }}>{signer.trim() || "your name"}</b>, have read and accept this agreement on behalf of <b style={{ color: entity ? INK : "#B0B0AC" }}>{entity.trim() || "the practice"}</b>, and bind it as the Covered Entity under version <b style={{ color: INK }}>{version || "…"}</b>. <span data-testid="baa-readpct" style={{ fontWeight: 700, color: atEnd ? "#1F7A46" : "#9A5A00" }}>{pct}% read</span>.
    </div>
  );

  const acceptActions = (
    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
      <button onClick={download} style={navBtn(false)} data-testid="baa-download">⤓ Download PDF</button>
      <span style={{ flex: 1 }} />
      <button onClick={() => setConfirm(true)} disabled={!canAccept} data-testid="baa-accept-btn"
        style={{ background: canAccept ? INK : "#E4E4E1", color: canAccept ? "#fff" : "#9B9B97", border: 0, borderRadius: 999, padding: "12px 20px", fontWeight: 800, fontSize: 13.5, cursor: canAccept ? "pointer" : "default" }}>
        {acceptLabel}
      </button>
    </div>
  );

  const confirmDialog = confirm ? (
    <div data-testid="baa-confirm" style={{ position: "fixed", inset: 0, zIndex: 9100, background: "rgba(20,20,18,.5)", backdropFilter: "blur(3px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
      onClick={(e) => { if (e.target === e.currentTarget && !busy) setConfirm(false); }}>
      <div style={{ background: "#fff", borderRadius: 18, width: "min(460px,96vw)", boxShadow: "0 30px 80px rgba(20,20,18,.35)", overflow: "hidden" }}>
        <div style={{ padding: "20px 22px 12px" }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: INK }}>Accept on behalf of this practice?</div>
          <div style={{ fontSize: 12.5, color: "#6C6C69", marginTop: 6, lineHeight: 1.5 }}>Accepting binds the practice named below as the Covered Entity under Aria One’s Business Associate Agreement, version {version}.</div>
        </div>
        <div style={{ margin: "0 22px", background: "#F7F7F6", borderRadius: 12, padding: "14px 16px" }}>
          <div style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".05em", color: "#9B9B97", fontWeight: 700 }}>Covered entity</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: INK, marginTop: 2 }} data-testid="baa-confirm-entity">{entity.trim() || "—"}</div>
          <div style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".05em", color: "#9B9B97", fontWeight: 700, marginTop: 12 }}>Signed by</div>
          <div style={{ fontSize: 13.5, fontWeight: 600, color: INK, marginTop: 2 }} data-testid="baa-confirm-signer">{signer.trim() || "—"}</div>
          <div style={{ fontSize: 11.5, color: "#6C6C69", marginTop: 12, lineHeight: 1.5 }}>
            Signed in as <b style={{ color: INK }}>{userEmail() || "your account"}</b> · {new Date().toLocaleString()} · recorded against version {version}. Your practice administrator can see this record at any time.
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "16px 22px 20px" }}>
          <button onClick={() => !busy && setConfirm(false)} style={{ ...navBtn(false), background: "transparent", border: 0 }} data-testid="baa-confirm-back">Back</button>
          <span style={{ flex: 1 }} />
          <button onClick={accept} disabled={busy} data-testid="baa-confirm-go"
            style={{ background: INK, color: "#fff", border: 0, borderRadius: 999, padding: "11px 20px", fontWeight: 800, fontSize: 13.5, cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1 }}>
            {busy ? "Recording…" : "Accept the agreement"}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  // ---- workspace layout (full-screen gate) ------------------------------
  if (workspace) {
    return (
      <div data-testid="baa-gate" style={{ position: "fixed", inset: 0, zIndex: 9000, background: "#F1F1EF", display: "flex", flexDirection: "column" }}>
        {/* sticky header */}
        <div style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 14, padding: "14px 26px", background: "#fff", borderBottom: "1px solid #E9E9E6" }}>
          <Mark size={30} />
          <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 10.5, fontWeight: 800, letterSpacing: ".05em", color: "#9A5A00", textTransform: "uppercase" }}>
              <span style={{ width: 6, height: 6, borderRadius: 999, background: "#FF9500" }} />Action required · compliance
            </span>
            <span style={{ fontSize: 16, fontWeight: 800, color: INK, letterSpacing: "-.01em" }}>Business Associate Agreement</span>
          </div>
          <span style={{ flex: 1 }} />
          <div style={{ textAlign: "right", fontSize: 11.5, color: "#6C6C69", lineHeight: 1.45, flexShrink: 0 }} data-testid="baa-version-stamp">
            {doc ? <>{doc.business_associate} · <b style={{ color: INK }}>v{doc.version}</b><br />effective {doc.effective_date}{reaccept ? <> · replaces v{status.accepted_version}</> : null}</> : "Loading…"}
          </div>
        </div>

        {/* scrolling agreement fills the workspace */}
        <div onScroll={onScroll} data-testid="baa-page-body" style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "22px 26px 40px" }}>
          <div style={{ maxWidth: 880, margin: "0 auto", display: "flex", flexDirection: "column", gap: 14 }}>
            {reacceptBanner}
            <div style={{ background: "#fff", border: "1px solid #E6E6E4", borderRadius: 16, padding: "28px 30px", lineHeight: 1.6, fontSize: 13.5, color: "#26261F" }}>
              <h2 style={{ fontSize: 19, fontWeight: 800, color: INK, margin: "0 0 4px", letterSpacing: "-.01em" }}>Business Associate Agreement</h2>
              <div style={{ fontSize: 12, color: "#6C6C69", margin: "0 0 18px" }}>
                {doc ? <>{doc.business_associate} · version {doc.version} · effective {doc.effective_date}{reaccept ? <> · replaces version {status.accepted_version}</> : null}</> : null}
              </div>
              {docContent}
            </div>
          </div>
        </div>

        {/* sticky sign footer */}
        <div style={{ flexShrink: 0, background: "#fff", borderTop: "1px solid #E9E9E6" }}>
          <div style={{ maxWidth: 880, margin: "0 auto", padding: "16px 26px 18px", display: "flex", flexDirection: "column", gap: 12 }}>
            {signFields}
            {attest}
            {err ? <div style={{ fontSize: 12, color: "#B3261E" }} data-testid="baa-error">{err}</div> : null}
            {acceptActions}
          </div>
        </div>

        {confirmDialog}
      </div>
    );
  }

  // ---- inline layout (Settings / Login) ---------------------------------
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, width: "100%" }} data-testid="baa-reader">
      <div>
        <div style={{ fontSize: 19, fontWeight: 800, color: INK, letterSpacing: "-.01em" }}>Business Associate Agreement</div>
        <div style={{ fontSize: 12, color: "#6C6C69", marginTop: 2 }}>
          {doc ? <>{doc.business_associate} · <b style={{ color: INK }}>v{doc.version}</b> · effective {doc.effective_date}{reaccept ? <> · replaces v{status.accepted_version}</> : null}</> : "Loading…"}
        </div>
      </div>

      {reacceptBanner}

      <div onScroll={showAccept ? onScroll : undefined} data-testid="baa-page-body"
        style={{ background: "#fff", border: "1px solid #E6E6E4", borderRadius: 14, padding: "20px 22px", maxHeight: showAccept ? "44vh" : 360, minHeight: 260, overflowY: "auto", lineHeight: 1.55, fontSize: 13.5, color: "#26261F" }}>
        {docContent}
      </div>

      {showAccept ? (
        <div style={{ borderTop: "1px solid #E6E6E4", paddingTop: 14, display: "flex", flexDirection: "column", gap: 12 }}>
          {signFields}
          {attest}
          {err ? <div style={{ fontSize: 12, color: "#B3261E" }} data-testid="baa-error">{err}</div> : null}
          {acceptActions}
        </div>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button onClick={download} style={navBtn(false)} data-testid="baa-download">⤓ Download PDF</button>
          <span style={{ flex: 1 }} />
          {onClose ? <button onClick={onClose} style={navBtn(false)} data-testid="baa-close">Close</button> : null}
        </div>
      )}

      {confirmDialog}
    </div>
  );
}

/* Global one-time gate: after login, blocks the app with a full-screen workspace until the current BAA is accepted. */
export default function BaaGate() {
  const [needs, setNeeds] = useState(false);
  const [entity, setEntity] = useState("");

  const check = useCallback(() => {
    const token = sessionStorage.getItem("revq.token");
    if (!token) { setNeeds(false); return; }
    api.get("/api/edi/baa/status")
      .then((r) => { setNeeds(!!r.data?.needs_acceptance); setEntity(r.data?.covered_entity_name || ""); })
      .catch(() => setNeeds(false));
  }, []);

  useEffect(() => {
    check();
    const onFocus = () => check();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [check]);

  if (!needs) return null;

  return <BaaReader showAccept variant="workspace" initialEntity={entity} onAccepted={() => setNeeds(false)} />;
}
